import {isUndefined} from 'lodash';
import React, {Component} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fonts} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../../libs/shared/ui/src/styles/mixins';
import {VariableHeightContextProps, withVariableHeight} from '../../scroll/variableHeightContext';

/*
 * Props.
 */

export interface MarkdownEditorProps {
  className?: string;
  value: string;
  onChange: HandlerOf<string>;
  textAreaRef?: HandlerOf<HTMLTextAreaElement | null>;
  shouldFocus?: boolean;
  /* Hint for the initial height of this editor, to help reduce visual jitter. */
  initialHeight?: number;
  onFocus?: HandlerOf<React.FocusEvent>;
  onBlur?: HandlerOf<React.FocusEvent>;
  onPaste?: HandlerOf<React.ClipboardEvent>;
  placeholder?: string;
}

/*
 * Style.
 */

const StyledTextareaAutoresize = styled(TextareaAutosize)`
  border: 0 none;
  padding: 0;
  background-color: transparent;
  width: 100%;
  resize: none;

  font-family: ${fonts.monospace};
  /* Make line-height same as HTML editor to reduce visual jitter when switching. */
  line-height: 1.4;
  font-size: 14px;

  ${textSelection()};
`;

/*
 * Component.
 */

class MarkdownEditorComponent extends Component<MarkdownEditorProps & VariableHeightContextProps> {
  constructor(props: MarkdownEditorProps & VariableHeightContextProps) {
    super(props);
    this.element = null;
  }

  private element: HTMLTextAreaElement | null;
  private previousHeight?: number;

  /*
   * Event handlers.
   */

  private readonly onChange: HandlerOf<React.ChangeEvent<HTMLTextAreaElement>> = (e) => {
    this.props.onChange(e.target.value);
  };

  private readonly onFirstWindowFocus: HandlerOf<FocusEvent> = () => {
    this.removeFirstWindowFocusListener();

    if (!this.props.shouldFocus) {
      return;
    }

    this.focus();
  };

  private readonly onHeightChange = (height: number) => {
    // Nothing to do if no one is listening.
    const {onHeightChange} = this.props;
    if (!onHeightChange) {
      return;
    }

    // Only report if the height has changed.
    if (!isUndefined(this.previousHeight) && this.previousHeight !== height) {
      onHeightChange(this.previousHeight, height);
    }

    // Keep track of the last height.
    this.previousHeight = height;
  };

  /*
   * Lifecycle.
   */

  componentDidMount() {
    if (!this.element) {
      return;
    }

    // Seed the height change tracker, because we don't get an initial onHeightChange() call.
    const {height} = this.element.getBoundingClientRect();
    this.previousHeight = height;

    if (!this.props.shouldFocus) {
      return;
    }

    // Wait for the window to receive focus, if necessary. Focusing the editor won't work otherwise.
    const document = this.element.ownerDocument;
    const window = document && document.defaultView;
    if (document && window && !document.hasFocus()) {
      window.addEventListener('focus', this.onFirstWindowFocus);
      return;
    }

    this.focus();
  }

  componentWillUnmount() {
    this.element = null;
    this.removeFirstWindowFocusListener();
    this.handleTextAreaChanged();
  }

  /*
   * Render.
   */

  render() {
    const {className, value, initialHeight, onFocus, onBlur, onPaste, placeholder} = this.props;

    return (
      <StyledTextareaAutoresize
        inputRef={(element) => {
          this.element = element;
          this.handleTextAreaChanged();
        }}
        className={className}
        // The autoresize behavior will override the height after the initial render.
        style={{height: initialHeight}}
        minRows={1}
        value={value}
        onChange={this.onChange}
        onHeightChange={this.onHeightChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={onPaste}
        placeholder={placeholder}
      />
    );
  }

  /*
   * Helpers.
   */

  private focus() {
    if (!this.element) {
      return;
    }

    this.element.focus();
  }

  private handleTextAreaChanged() {
    const {textAreaRef} = this.props;
    if (textAreaRef) {
      textAreaRef(this.element);
    }
  }

  private removeFirstWindowFocusListener() {
    if (!this.element) {
      return;
    }

    const document = this.element.ownerDocument;
    const window = document && document.defaultView;
    if (!window) {
      return;
    }

    window.removeEventListener('focus', this.onFirstWindowFocus);
  }
}

export const MarkdownEditor = withVariableHeight(MarkdownEditorComponent);
